// Single source of truth for site-wide SEO defaults. Imported by route
// `head()` definitions and by the build-time per-route HTML emitter.

export const SITE_URL = 'https://prodbattle.com';
export const SITE_NAME = 'Prod Battle';
export const SITE_TAGLINE = 'Online Beat Battles for Music Producers';
export const DEFAULT_DESCRIPTION =
  'Online beat battle platform for music producers. Same sample kit for everyone in a match - skill, not gear. Quickplay, ranked, private rooms, weekly tournaments. Free to play.';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const TWITTER_HANDLE = '@prodbattle';

export type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { httpEquiv: string; content: string }
  // TanStack Router special-cases this key in the meta array and emits a
  // <script type="application/ld+json"> with the JSON-stringified object.
  | { 'script:ld+json': Record<string, unknown> };

export type LinkTag = { rel: string; href: string; sizes?: string; type?: string };

// We type `meta`/`links` as `any[]` because TanStack Router's runtime accepts
// our extended shape (`{ title }`, `{ 'script:ld+json' }`) but its TypeScript
// surface narrows to HTMLMetaElement attrs only - any narrower union here
// fails to satisfy TanStack's route-option type. The internal MetaTag/LinkTag
// types still document the shape we actually emit.
export type RouteHead = {
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  meta: any[];
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  links?: any[];
};

type Opts = {
  title: string;
  description?: string;
  path: string;
  image?: string;
  noindex?: boolean;
  type?: 'website' | 'article' | 'profile';
};

// Build a full route head with the right title format, canonical URL, and
// OG/Twitter cards. Pass `path` as the absolute path beginning with `/`.
export function buildHead({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  type = 'website',
}: Opts): RouteHead {
  const fullTitle = path === '/' ? `${SITE_NAME} - ${SITE_TAGLINE}` : `${title} - ${SITE_NAME}`;
  const url = `${SITE_URL}${path}`;
  return {
    meta: [
      { title: fullTitle },
      { name: 'description', content: description },
      ...(noindex ? [{ name: 'robots', content: 'noindex, nofollow' } as MetaTag] : []),
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: description },
      { property: 'og:type', content: type },
      { property: 'og:url', content: url },
      { property: 'og:image', content: image },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:site_name', content: SITE_NAME },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: TWITTER_HANDLE },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
    ],
    links: [{ rel: 'canonical', href: url }],
  };
}
